import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import { signInWithEmailAndPassword, signOut } from 'firebase/auth';
import { auth } from '../firebase/config';
import { registerWithEmail, loginWithEmail, resendEmailVerification, resetPassword } from '../firebase/auth';
import { checkRegistrationRestriction } from '../utils/userUtils';
import { claimCoupon, getCouponErrorMessage, isRetryableCouponError } from '../api/couponService';
import { getApiUrl, getApiBaseUrl } from '../utils/apiConfig';
// 📊 이벤트 트래킹 (Firestore 기반)
import {
  trackFreeTrialStart,
  trackSignupAttempt,
  trackSignupCompleteOnce,
  trackSignupPageView,
  trackInAppBrowserEvent,
  trackFestaEvent,
} from '../utils/eventTracker';
import { FESTA_COUPON_CODE } from '../utils/festaCampaign';
// 📊 GA4 이벤트 트래킹
import { trackSignUp, trackLogin } from '../utils/analytics';
// 🎁 회원가입 직후 7일 무료 체험 자동 시작
import {
  triggerPendingFreeTrialNow,
  hasPendingFreeTrial,
  clearPendingFreeTrial,
  trackPendingFreeTrialMetaPixel,
} from '../utils/freeTrialPending';
import { getMetaMatchParams } from '../utils/analytics';
import { getPasswordChecks, validatePassword } from '../utils/passwordPolicy';
import { suggestEmailCorrection } from '../utils/emailTypo';
import { useToast } from '../components/Toast';
import { saveClassPhotoTrialWelcome } from '../utils/classPhotoTrialWelcome';
import InAppBrowserLoginSheet from '../components/auth/InAppBrowserLoginSheet';
import {
  AI30_COUPON_CODE,
  capturePendingCouponFromUrl,
  showAi30AppliedToastOnce,
} from '../utils/ai30Campaign';
import {
  IN_APP_BROWSER_AUTH_CODE,
  getInAppBrowserName,
  isEmbeddedWebView,
} from '../utils/inAppBrowser';
import './AuthPage.css';

const WRONG_CREDENTIAL_MESSAGE = '이메일 또는 비밀번호가 맞지 않아요. Google로 가입했다면 Google로 계속하기를 눌러주세요.';
const EMAIL_IN_USE_MESSAGE = '이미 가입된 이메일이에요. 로그인해서 이어서 사용하세요.';
const RESEND_COOLDOWN_SECONDS = 60;

const COMMON_AUTH_ERROR_MESSAGES = {
  'auth/invalid-email': '이메일 형식을 확인해주세요.',
  'auth/network-request-failed': '인터넷 연결을 확인하고 다시 시도해주세요.',
  'auth/too-many-requests': '시도가 많아 잠시 막혔어요. 몇 분 뒤 다시 시도해주세요.',
};

const LOGIN_ERROR_MESSAGES = {
  ...COMMON_AUTH_ERROR_MESSAGES,
  'auth/user-not-found': '가입되지 않은 이메일이에요. 회원가입을 먼저 해주세요.',
  'auth/wrong-password': WRONG_CREDENTIAL_MESSAGE,
  // 이메일 열거 보호가 켜진 프로젝트는 계정 없음·비밀번호 틀림을 모두 이 코드로 돌려준다.
  'auth/invalid-credential': WRONG_CREDENTIAL_MESSAGE,
  'auth/invalid-login-credentials': WRONG_CREDENTIAL_MESSAGE,
  'auth/user-disabled': '사용이 중지된 계정이에요. angel@plactical.com으로 문의해주세요.',
  'auth/too-many-requests': '로그인 시도가 많아 잠시 막혔어요. 몇 분 뒤 다시 하거나 비밀번호를 재설정해주세요.',
};

const SIGNUP_ERROR_MESSAGES = {
  ...COMMON_AUTH_ERROR_MESSAGES,
  'auth/email-already-in-use': EMAIL_IN_USE_MESSAGE,
  'auth/weak-password': '비밀번호가 너무 약해요. 8자 이상으로 설정해주세요.',
};

export const getLoginErrorMessage = (code) => (
  LOGIN_ERROR_MESSAGES[code] || '로그인에 실패했어요. 다시 시도해주세요.'
);

export const getSignupErrorMessage = (code) => (
  SIGNUP_ERROR_MESSAGES[code] || '회원가입에 실패했어요. 잠시 후 다시 시도해주세요.'
);

export const normalizePostAuthRedirect = (value) => {
  if (!value) return '/home';
  try {
    const target = new URL(value, window.location.origin);
    if (target.origin !== window.location.origin) return '/home';
    return `${target.pathname}${target.search}${target.hash}` || '/home';
  } catch (_) {
    return '/home';
  }
};

const AuthPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { signInWithGoogle, currentUser } = useAuth();
  const { fetchSubscriptionStatus } = useSubscription();
  const { showToast } = useToast();
  // 인증 성공 후 자동 redirect를 한 번만 수행하기 위한 가드 (StrictMode/배칭 중복 방지)
  const postAuthHandledRef = useRef(false);
  const pendingCouponPromiseRef = useRef(null);
  const signupPageTrackedRef = useRef(false);
  
  // 탭 상태 관리
  const [activeTab, setActiveTab] = useState('login'); // 'login' or 'signup'
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showTossLogin, setShowTossLogin] = useState(false);
  
  // 이메일 폼 상태
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [emailFormData, setEmailFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    displayName: ''
  });
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [emailTouched, setEmailTouched] = useState(false);
  const [authNotice, setAuthNotice] = useState('');
  
  // 약관 동의 상태 (이메일 회원가입용)
  const [termsAgreed, setTermsAgreed] = useState(false);
  const [privacyAgreed, setPrivacyAgreed] = useState(false);
  const [marketingAgreed, setMarketingAgreed] = useState(false);
  
  // 이메일 인증 상태
  const [showEmailVerification, setShowEmailVerification] = useState(false);
  const [verificationEmail, setVerificationEmail] = useState('');
  const [resendLoading, setResendLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [verificationNotice, setVerificationNotice] = useState(null);
  
  // 토스 전용 로그인 폼 상태
  const [tossEmail, setTossEmail] = useState('');
  const [tossPassword, setTossPassword] = useState('');
  const [tossLoading, setTossLoading] = useState(false);
  
  // 재가입 제한 모달 상태
  const [showRestrictionModal, setShowRestrictionModal] = useState(false);
  const [restrictionInfo, setRestrictionInfo] = useState(null);
  
  // 비밀번호 찾기 상태
  const [showCompanyInfo, setShowCompanyInfo] = useState(false);
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);
  const [showInAppLoginSheet, setShowInAppLoginSheet] = useState(false);
  // 인앱 브라우저(인스타·카톡 등)는 Google 로그인이 막혀서 이메일을 먼저 보여준다.
  const [emailFirst] = useState(() => (
    typeof navigator !== 'undefined' && isEmbeddedWebView(navigator.userAgent)
  ));

  const getRedirectPath = () => {
    const urlParams = new URLSearchParams(location.search);
    return normalizePostAuthRedirect(
      urlParams.get('redirect') || location.state?.redirect,
    );
  };
  
  // 🎁 비로그인 사용자가 7일 무료 체험 CTA를 누르고 회원가입한 경우, 명시적으로 자동 시작.
  // FreeTrialAutoStarter는 페이지 어디서든 작동하는 백업 안전망이지만, AuthPage가 인증
  // 성공을 가장 빨리 인지하는 지점이므로 여기서 직접 호출하여 React effect 타이밍에 대한
  // 의존성을 제거하고 100% 신뢰성을 보장한다.
  const startPendingFreeTrialIfAny = useCallback(async (user) => {
    if (!user) return false;
    if (!hasPendingFreeTrial()) return false;

    let result = null;
    for (let attempt = 0; attempt < 3; attempt += 1) {
      result = await triggerPendingFreeTrialNow(user, { getApiBaseUrl, getMetaMatchParams });
      if (result.success || !result.retryable) break;
      await new Promise((resolve) => setTimeout(resolve, 300 * (attempt + 1)));
    }

    if (result?.success) {
      trackFreeTrialStart(result.triggerFeature);
      trackPendingFreeTrialMetaPixel(result);
      return result;
    }
    if (result?.error && !result?.skipped) {
      showToast(`가입은 완료됐어요. ${result.error}`, 'warning', 6000);
    }
    return result || { success: false, skipped: true };
  }, [showToast]);

  // 인증 성공 후 공통 redirect 처리: pending 체험이 있으면 시작하고 토스트 노출 후 full reload,
  // 아니면 일반 navigate. 모든 인증 핸들러(Google/이메일/토스/이메일 인증 polling)에서 호출되어
  // navigate 직전 AuthPage가 unmount되더라도 누락 없이 동작한다. postAuthHandledRef로
  // 한 번만 실행되도록 가드.
  const handlePostAuthRedirect = useCallback(async (user) => {
    if (postAuthHandledRef.current) return;
    postAuthHandledRef.current = true;

    const redirectPath = getRedirectPath();
    const trialResult = await startPendingFreeTrialIfAny(user);

    if (trialResult?.success) {
      await fetchSubscriptionStatus(true);
      saveClassPhotoTrialWelcome({
        triggerFeature: trialResult.triggerFeature,
        redirectPath,
      });
      window.location.replace(redirectPath);
    } else {
      navigate(redirectPath);
    }
  }, [fetchSubscriptionStatus, navigate, startPendingFreeTrialIfAny]); // eslint-disable-line react-hooks/exhaustive-deps

  const registerPendingCoupon = useCallback(async (user) => {
    if (pendingCouponPromiseRef.current) {
      return pendingCouponPromiseRef.current;
    }

    const pendingCoupon = localStorage.getItem('pending_coupon_code');
    if (!pendingCoupon || !user) {
      return { success: false, skipped: true };
    }

    // 쿠폰 자동 등록이 우선이다. 7일 무료체험 pending이 같이 남아 있으면
    // 인증 후 두 혜택이 동시에 실행될 수 있으므로 쿠폰 흐름에서 제거한다.
    clearPendingFreeTrial();

    const run = (async () => {
      try {
        const result = await claimCoupon(pendingCoupon, user, { source: 'auto_register', url: window.location.href });
        localStorage.removeItem('pending_coupon_code');
        await fetchSubscriptionStatus(true);

        const normalizedCoupon = String(pendingCoupon).trim().toUpperCase();
        const isAi30 = normalizedCoupon === AI30_COUPON_CODE;
        const alreadyGranted = result?.already_granted === true || result?.already_claimed === true;
        if (normalizedCoupon === FESTA_COUPON_CODE) {
          trackFestaEvent(alreadyGranted ? 'trial_blocked' : 'trial_applied', {
            stage: 'auth',
            entry: alreadyGranted ? 'already_claimed' : 'new',
          });
        }
        if (isAi30) {
          showAi30AppliedToastOnce(showToast);
        } else {
          const benefitDays = result?.benefit_days || 30;
          const message = alreadyGranted
            ? '이미 PRO 혜택이 적용되어 있어요. 바로 이용해보세요.'
            : `${benefitDays}일 PRO 혜택이 시작됐어요. 바로 이용해보세요.`;
          showToast(message, 'success', 6000);
        }

        return { success: true, result };
      } catch (error) {
        console.error('쿠폰 자동 등록 실패:', error);
        if (!isRetryableCouponError(error)) localStorage.removeItem('pending_coupon_code');
        if (String(pendingCoupon).trim().toUpperCase() === FESTA_COUPON_CODE) {
          trackFestaEvent('trial_blocked', { stage: 'auth', entry: 'error' });
        }

        // already_claimed=true 응답은 throw가 아니라 success 경로로 처리되므로
        // 이 catch 블록에 들어오는 건 진짜 에러 케이스(존재하지 않음/만료/수량 소진/그룹 중복 등).
        // 기존에는 errorMsg.includes('이미')로 광범위 매칭해 그룹 중복 같은 다른 에러까지
        // "이미 등록된 쿠폰입니다"로 잘못 안내됐다. getCouponErrorMessage로 일관된 사용자
        // 친화 메시지로 변환해 안내한다.
        const friendly = getCouponErrorMessage(error);
        showToast(`쿠폰 자동 등록을 진행할 수 없어요.\n${friendly}`, 'error', 6000);
        return { success: false, error };
      } finally {
        pendingCouponPromiseRef.current = null;
      }
    })();

    pendingCouponPromiseRef.current = run;
    return run;
  }, [fetchSubscriptionStatus, showToast]);

  useEffect(() => {
    // currentUser가 null로 떨어지면 ref 자동 리셋 — 같은 세션에서 로그아웃 후
    // 다른 계정으로 재로그인하는 경우에도 자동 트리거가 정상 작동하도록 보장.
    if (!currentUser) {
      postAuthHandledRef.current = false;
    }

    if (currentUser) {
      // 재가입 제한 모달이 떠 있는 동안에는 자동 redirect/체험 시작 보류.
      // (Google 로그인 → restrictionCheck → 제한 발견 → signOut 흐름에서
      //  일시적으로 currentUser가 살아있는 race window 방지)
      if (showRestrictionModal) return;

      if (currentUser.emailVerified || currentUser.providerData[0]?.providerId === 'google.com') {
        let cancelled = false;
        (async () => {
          await registerPendingCoupon(currentUser);
          if (!cancelled) {
            await handlePostAuthRedirect(currentUser);
          }
        })();
        return () => {
          cancelled = true;
        };
      }
    }

    const urlParams = new URLSearchParams(location.search);
    capturePendingCouponFromUrl(location.search);
    const isTossReview = urlParams.get('toss_review') === 'true';
    const tabParam = urlParams.get('tab');
    const isVerifiedReturn = urlParams.get('verified') === 'true';
    const isPasswordResetReturn = urlParams.get('reset') === 'true';
    
    setShowTossLogin(isTossReview);
    
    if (tabParam === 'login' || tabParam === 'signup') {
      setActiveTab(tabParam);
    }

    if (isVerifiedReturn) {
      setActiveTab('login');
      setShowEmailForm(false);
      setShowPasswordReset(false);
      setShowEmailVerification(false);
      setEmailError('');
      setAuthNotice('이메일 인증이 완료됐어요. 로그인해서 이어서 사용하세요.');
    } else if (isPasswordResetReturn) {
      setActiveTab('login');
      setShowEmailForm(false);
      setShowPasswordReset(false);
      setEmailError('');
      setAuthNotice('비밀번호를 다시 설정했어요. 새 비밀번호로 로그인해주세요.');
    }
    
    if (location.state?.mode) {
      setActiveTab(location.state.mode === 'signup' ? 'signup' : 'login');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, navigate, location.state, location.search, showRestrictionModal]);

  // 같은 브라우저의 다른 탭에서 인증 링크를 열면 세션은 살아 있지만 emailVerified 값이
  // 예전 그대로라, 새로 읽어 와서 바로 이어서 들어가게 한다.
  useEffect(() => {
    const isVerifiedReturn = new URLSearchParams(location.search).get('verified') === 'true';
    if (!isVerifiedReturn || !currentUser || currentUser.emailVerified) return undefined;
    let cancelled = false;
    (async () => {
      try {
        await currentUser.reload();
      } catch (_) {
        return;
      }
      if (cancelled || !currentUser.emailVerified) return;
      await registerPendingCoupon(currentUser);
      if (!cancelled) await handlePostAuthRedirect(currentUser);
    })();
    return () => {
      cancelled = true;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, location.search]);

  useEffect(() => {
    if (resendCooldown <= 0) return undefined;
    const timer = setTimeout(() => setResendCooldown((seconds) => seconds - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  useEffect(() => {
    if (currentUser && showEmailVerification) {
      const checkEmailVerification = setInterval(async () => {
        try {
          await currentUser.reload();
        } catch (_) {
          return;
        }
        if (currentUser.emailVerified) {
          clearInterval(checkEmailVerification);
          setShowEmailVerification(false);
          setEmailError('');
          await registerPendingCoupon(currentUser);
          await handlePostAuthRedirect(currentUser);
        }
      }, 2000);

      return () => clearInterval(checkEmailVerification);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, showEmailVerification, navigate]);

  useEffect(() => {
    if (activeTab !== 'signup' || signupPageTrackedRef.current) return;
    const params = new URLSearchParams(location.search);
    trackSignupPageView(
      params.get('utm_source') || params.get('source') || 'auth_page'
    );
    signupPageTrackedRef.current = true;
  }, [activeTab, location.search]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setError('');
    setEmailError('');
    setAuthNotice('');
    setShowEmailForm(false);
    setEmailFormData({ email: '', password: '', confirmPassword: '', displayName: '' });
    setTermsAgreed(false);
    setPrivacyAgreed(false);
    setShowPasswordReset(false);
    setResetEmail('');
    setResetSuccess(false);
    
    const urlParams = new URLSearchParams(location.search);
    urlParams.set('tab', tab);
    navigate(`/auth?${urlParams.toString()}`, { replace: true });
  };

  const switchToLoginWithEmail = () => {
    const { email } = emailFormData;
    handleTabChange('login');
    setEmailFormData((prev) => ({ ...prev, email }));
    setShowEmailForm(true);
  };

  const handleEmailFormChange = (field, value) => {
    setEmailFormData(prev => ({ ...prev, [field]: value }));
    if (field === 'email') setEmailTouched(false);
  };

  const emailSuggestion = emailTouched ? suggestEmailCorrection(emailFormData.email) : null;
  const passwordChecks = getPasswordChecks(emailFormData.password);
  const confirmPassword = emailFormData.confirmPassword;
  const passwordsMatch = confirmPassword.length > 0 && confirmPassword === emailFormData.password;

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    const { email, password } = emailFormData;
    
    if (!email || !password) {
      setEmailError('이메일과 비밀번호를 입력해주세요.');
      return;
    }
    
    try {
      setEmailLoading(true);
      setEmailError('');
      
      const result = await loginWithEmail(email, password);
      
      if (result.error) {
        setEmailError(getLoginErrorMessage(result.error.code));
      } else {
        if (!result.user.emailVerified) {
          setVerificationEmail(email);
          setShowEmailForm(false);
          setShowEmailVerification(true);
          setVerificationNotice(null);
          setEmailError('');
          return;
        }
        trackLogin('email');
        await registerPendingCoupon(result.user);
        await handlePostAuthRedirect(result.user);
      }
    } catch (error) {
      console.error('이메일 로그인 실패:', error);
      setEmailError('로그인 중 문제가 발생했어요. 잠시 후 다시 시도해주세요.');
    } finally {
      setEmailLoading(false);
    }
  };

  const handleEmailSignup = async (e) => {
    e.preventDefault();
    const { email, password, confirmPassword, displayName } = emailFormData;
    
    if (!email || !password || !confirmPassword || !displayName) {
      setEmailError('모든 항목을 입력해주세요.');
      return;
    }
    
    if (password !== confirmPassword) {
      setEmailError('비밀번호가 일치하지 않아요.');
      return;
    }
    
    const passwordError = validatePassword(password);
    if (passwordError) {
      setEmailError(passwordError);
      return;
    }
    
    if (!termsAgreed || !privacyAgreed) {
      setEmailError('이용약관과 개인정보처리방침에 동의해주세요.');
      return;
    }
    
    try {
      setEmailLoading(true);
      setEmailError('');
      trackSignupAttempt('email');

      const restrictionCheck = await checkRegistrationRestriction(email);
      
      if (restrictionCheck.restriction.restricted) {
        setEmailError(restrictionCheck.restriction.message);
        return;
      }
      
      const result = await registerWithEmail(email, password, displayName, marketingAgreed);
      
      if (result.error) {
        setEmailError(getSignupErrorMessage(result.error.code));
      } else {
        trackSignupCompleteOnce(result.user?.uid || auth.currentUser?.uid, 'email', 'auth_page');
        trackSignUp('email');
        setVerificationEmail(email);
        setShowEmailForm(false);
        setShowEmailVerification(true);
        setEmailError('');
        if (result.emailSent === false) {
          setVerificationNotice({ type: 'error', text: '가입은 됐는데 인증 메일을 아직 못 보냈어요. 아래 재발송을 눌러주세요.' });
          setResendCooldown(0);
        } else {
          setVerificationNotice(null);
          setResendCooldown(RESEND_COOLDOWN_SECONDS);
        }
      }
    } catch (error) {
      console.error('이메일 회원가입 실패:', error);
      setEmailError('회원가입 중 문제가 발생했어요. 잠시 후 다시 시도해주세요.');
    } finally {
      setEmailLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    try {
      setIsLoading(true);
      setError('');
      if (activeTab === 'signup') {
        trackSignupAttempt('google');
      }

      const authResult = await signInWithGoogle();
      const user = authResult?.user || authResult;
      const isNewUser = Boolean(authResult?.isNewUser);
      
      if (user && user.email) {
        const restrictionCheck = await checkRegistrationRestriction(user.email);
        
        if (restrictionCheck.restriction?.restricted) {
          setRestrictionInfo({
            daysRemaining: restrictionCheck.restriction.days_remaining,
            deletedAt: restrictionCheck.restriction.deleted_at,
            email: user.email
          });
          setShowRestrictionModal(true);
          
          try {
            const idToken = await user.getIdToken();
            await fetch(getApiUrl('/api/user/delete-restricted-account'), {
              method: 'DELETE',
              headers: {
                'Authorization': `Bearer ${idToken}`,
                'Content-Type': 'application/json'
              }
            });
          } catch (deleteError) {
            console.error('제한된 계정 삭제 실패:', deleteError);
          }
          
          await signOut(auth);
          return;
        }
      }
      
      if (isNewUser) {
        trackSignupCompleteOnce(user?.uid, 'google', 'auth_page');
        trackSignUp('google');
      }
      trackLogin('google');
      await registerPendingCoupon(user);
      await handlePostAuthRedirect(user);
    } catch (error) {
      if (error?.code === IN_APP_BROWSER_AUTH_CODE) {
        trackInAppBrowserEvent('login_blocked', {
          browser: getInAppBrowserName() || 'unknown',
          entry: activeTab,
        });
        setShowInAppLoginSheet(true);
        setError('');
        return;
      }
      console.error('Google 로그인 실패:', error);
      setError('로그인에 실패했어요. 다시 시도해주세요.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleTossLogin = async (e) => {
    e.preventDefault();
    try {
      setTossLoading(true);
      setError('');
      const result = await signInWithEmailAndPassword(auth, tossEmail, tossPassword);
      await registerPendingCoupon(result.user);
      await handlePostAuthRedirect(result.user);
    } catch (error) {
      console.error('토스 로그인 실패:', error);
      setError('로그인에 실패했어요. 이메일과 비밀번호를 확인해주세요.');
    } finally {
      setTossLoading(false);
    }
  };

  const handleResendVerification = async () => {
    if (resendCooldown > 0) return;
    try {
      setResendLoading(true);
      setVerificationNotice(null);
      const result = await resendEmailVerification();
      if (result.error) {
        setVerificationNotice({
          type: 'error',
          text: result.error.status === 429
            ? '메일을 여러 번 보내서 잠시 막혔어요. 이미 받은 메일을 먼저 확인하고, 30분 뒤 다시 시도해주세요.'
            : '인증 메일을 보내지 못했어요. 잠시 후 다시 시도해주세요.',
        });
      } else if (result.alreadyVerified) {
        setVerificationNotice({ type: 'success', text: '이미 인증된 계정이에요. 잠시 후 자동으로 이동해요.' });
      } else {
        setVerificationNotice({ type: 'success', text: '인증 메일을 다시 보냈어요. 메일함을 확인해주세요.' });
        setResendCooldown(RESEND_COOLDOWN_SECONDS);
      }
    } catch (error) {
      console.error('인증 메일 재발송 실패:', error);
      setVerificationNotice({ type: 'error', text: '인증 메일을 보내지 못했어요. 잠시 후 다시 시도해주세요.' });
    } finally {
      setResendLoading(false);
    }
  };

  const handleBackToLanding = () => {
    navigate('/');
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    if (!resetEmail) {
      setEmailError('이메일을 입력해주세요.');
      return;
    }
    try {
      setResetLoading(true);
      setEmailError('');
      const result = await resetPassword(resetEmail);
      if (result.error) {
        if (result.error.code === 'auth/user-not-found') {
          setEmailError('등록되지 않은 이메일이에요.');
        } else if (result.error.code === 'auth/invalid-email') {
          setEmailError('이메일 형식을 확인해주세요.');
        } else {
          setEmailError('비밀번호 재설정 메일 전송에 실패했어요.');
        }
      } else {
        setResetSuccess(true);
        setEmailError('');
      }
    } catch (error) {
      console.error('비밀번호 재설정 오류:', error);
      setEmailError('비밀번호 재설정 중 문제가 발생했어요.');
    } finally {
      setResetLoading(false);
    }
  };

  const emailButtonClassName = `auth-email-btn${emailFirst ? ' auth-email-btn--primary' : ''}`;
  // 인증 안내가 떠 있을 때는 다른 로그인 버튼을 숨겨 "메일 확인"만 보이게 한다.
  const showMethodButtons = !showEmailVerification;
  const googleButton = (
    <button
      onClick={handleGoogleSignIn}
      disabled={isLoading}
      className="auth-google-btn"
    >
      <svg width="20" height="20" viewBox="0 0 24 24">
        <path fill="#4285f4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
        <path fill="#34a853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
        <path fill="#fbbc05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
        <path fill="#ea4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
      </svg>
      {isLoading ? '처리 중...' : `Google로 ${activeTab === 'signup' ? '시작하기' : '계속하기'}`}
    </button>
  );
  const methodDivider = (
    <div className="auth-divider">
      <div className="auth-divider__line"></div>
      <span className="auth-divider__text">또는</span>
      <div className="auth-divider__line"></div>
    </div>
  );

  return (
    <div className="auth-page">
      <Helmet>
        <title>{activeTab === 'signup' ? '회원가입' : '로그인'} | 교사 전용 AI - 생기부·PPT·학습지·이미지 | 왓퀴즈</title>
        <meta name="description" content="왓퀴즈 교사 전용 AI 플랫폼. 생기부 3분 작성, AI PPT 만들기, 학습지 자동 생성, 이미지 생성, 수행평가 채점. 구글 계정으로 10초 만에 시작하세요." />
        <link rel="canonical" href="https://whatquiz.co.kr/login" />
      </Helmet>
      {/* 헤더 */}
      <div className="auth-header">
        <button onClick={handleBackToLanding} className="auth-header__back">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="15 18 9 12 15 6"/>
          </svg>
          돌아가기
        </button>
        <div className="auth-header__logo">왓퀴즈</div>
      </div>

      {/* 메인 컨텐츠 */}
      <div className="auth-main">
        <div className="auth-card">
          {/* 탭 헤더 */}
          <div className="auth-tabs">
            <button
              onClick={() => handleTabChange('login')}
              className={`auth-tab ${activeTab === 'login' ? 'auth-tab--active' : ''}`}
            >
              로그인
            </button>
            <button
              onClick={() => handleTabChange('signup')}
              className={`auth-tab ${activeTab === 'signup' ? 'auth-tab--active' : ''}`}
            >
              회원가입
            </button>
          </div>

          <h1 className="auth-title">
            {activeTab === 'signup' ? '왓퀴즈 시작하기' : '다시 오셨군요'}
          </h1>
          <p className="auth-subtitle">
            {activeTab === 'signup' 
              ? 'AI가 수업 준비를 도와드려요' 
              : '로그인하고 이어서 사용하세요'
            }
          </p>

          {/* 에러 메시지 */}
          {error && <div className="auth-error">{error}</div>}
          {authNotice && activeTab === 'login' && <div className="auth-success">{authNotice}</div>}

          {showMethodButtons && emailFirst && !showEmailForm && (
            <p className="auth-inapp-hint">앱 안에서 열린 화면이라 이메일로 시작하는 게 가장 빨라요</p>
          )}

          {showMethodButtons && !emailFirst && (
            <>
              {googleButton}
              {methodDivider}
            </>
          )}

          {/* 이메일 로그인 버튼 */}
          {showMethodButtons && activeTab === 'login' && !showEmailForm && (
            <>
              <button
                onClick={() => { setShowEmailForm(true); setEmailError(''); }}
                className={emailButtonClassName}
              >
                이메일로 로그인
              </button>
              {!showPasswordReset && (
                <div style={{ textAlign: 'center', marginBottom: '16px' }}>
                  <button
                    onClick={() => { setShowPasswordReset(true); setEmailError(''); setResetSuccess(false); }}
                    className="auth-forgot-link"
                  >
                    비밀번호를 잊었나요?
                  </button>
                </div>
              )}
            </>
          )}

          {/* 이메일 회원가입 버튼 */}
          {showMethodButtons && activeTab === 'signup' && !showEmailForm && (
            <button
              onClick={() => { setShowEmailForm(true); setEmailError(''); }}
              className={emailButtonClassName}
            >
              이메일로 회원가입
            </button>
          )}

          {/* 이메일 폼 */}
          {showEmailForm && (
            <div className="auth-form">
              <form onSubmit={activeTab === 'login' ? handleEmailLogin : handleEmailSignup}>
                {/* 닉네임 (회원가입만) */}
                {activeTab === 'signup' && (
                  <div className="auth-field">
                    <label className="auth-field__label">닉네임</label>
                    <input
                      type="text"
                      value={emailFormData.displayName}
                      onChange={(e) => handleEmailFormChange('displayName', e.target.value)}
                      placeholder="사용할 닉네임"
                      required
                      className="auth-field__input"
                    />
                  </div>
                )}

                <div className="auth-field">
                  <label className="auth-field__label">이메일</label>
                  <input
                    type="email"
                    value={emailFormData.email}
                    onChange={(e) => handleEmailFormChange('email', e.target.value)}
                    onBlur={() => setEmailTouched(true)}
                    placeholder="example@email.com"
                    required
                    className="auth-field__input"
                  />
                  {emailSuggestion && (
                    <p className="auth-field__suggest" role="status">
                      혹시 <b>{emailSuggestion}</b> 인가요?
                      <button
                        type="button"
                        onClick={() => {
                          setEmailFormData((prev) => ({ ...prev, email: emailSuggestion }));
                          setEmailTouched(false);
                        }}
                      >
                        이걸로 고치기
                      </button>
                    </p>
                  )}
                </div>

                <div className="auth-field">
                  <label className="auth-field__label">비밀번호</label>
                  <input
                    type="password"
                    value={emailFormData.password}
                    onChange={(e) => handleEmailFormChange('password', e.target.value)}
                    placeholder={activeTab === 'signup' ? '8자 이상, 영문+숫자+특수문자' : '비밀번호'}
                    required
                    className="auth-field__input"
                  />
                  {activeTab === 'signup' && (
                    <ul className="auth-pw-checks" aria-label="비밀번호 조건">
                      {passwordChecks.map((check) => (
                        <li key={check.id} className={check.ok ? 'is-ok' : ''}>
                          <span aria-hidden="true">{check.ok ? '✓' : '·'}</span>
                          {check.label}
                          <span className="auth-sr-only">{check.ok ? ' 충족' : ' 필요'}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  {activeTab === 'login' && (
                    <div className="auth-field__helper">
                      <button
                        type="button"
                        onClick={() => { setShowPasswordReset(true); setShowEmailForm(false); setEmailError(''); setResetSuccess(false); }}
                        className="auth-field__helper-link"
                      >
                        비밀번호를 잊었나요?
                      </button>
                    </div>
                  )}
                </div>

                {/* 비밀번호 확인 (회원가입만) */}
                {activeTab === 'signup' && (
                  <div className="auth-field">
                    <label className="auth-field__label">비밀번호 확인</label>
                    <input
                      type="password"
                      value={emailFormData.confirmPassword}
                      onChange={(e) => handleEmailFormChange('confirmPassword', e.target.value)}
                      placeholder="비밀번호를 다시 입력하세요"
                      required
                      className="auth-field__input"
                    />
                    {confirmPassword.length > 0 && (
                      <p className={`auth-field__match${passwordsMatch ? ' is-ok' : ''}`}>
                        {passwordsMatch ? '✓ 비밀번호가 같아요' : '비밀번호가 아직 달라요'}
                      </p>
                    )}
                  </div>
                )}

                {/* 약관 동의 (이메일 회원가입만) */}
                {activeTab === 'signup' && (
                  <div className="auth-terms">
                    <div className="auth-terms__title">
                      <span className="auth-terms__required">*</span> 필수 약관 동의
                    </div>
                    
                    <div className="auth-terms__item">
                      <label className="auth-terms__checkbox-label">
                        <input
                          type="checkbox"
                          checked={termsAgreed}
                          onChange={(e) => setTermsAgreed(e.target.checked)}
                          className="auth-terms__checkbox"
                        />
                        <span>
                          <button type="button" onClick={() => window.open('/terms-of-service', '_blank')} className="auth-terms__link">이용약관</button>에 동의해요.
                        </span>
                      </label>
                    </div>
                    
                    <div className="auth-terms__item">
                      <label className="auth-terms__checkbox-label">
                        <input
                          type="checkbox"
                          checked={privacyAgreed}
                          onChange={(e) => setPrivacyAgreed(e.target.checked)}
                          className="auth-terms__checkbox"
                        />
                        <span>
                          <button type="button" onClick={() => window.open('/privacy-policy', '_blank')} className="auth-terms__link">개인정보처리방침</button>에 동의해요.
                        </span>
                      </label>
                    </div>
                    
                    <div className="auth-terms__item auth-terms__item--marketing">
                      <div className="auth-terms__item-label">선택사항</div>
                      <label className="auth-terms__checkbox-label">
                        <input
                          type="checkbox"
                          checked={marketingAgreed}
                          onChange={(e) => setMarketingAgreed(e.target.checked)}
                          className="auth-terms__checkbox"
                        />
                        <span>마케팅 정보 수신에 동의해요.</span>
                      </label>
                      <div className="auth-terms__marketing-desc">
                        이벤트, 신기능, 교육 팁 등 유용한 정보를 보내드려요.
                      </div>
                    </div>
                  </div>
                )}

                {/* 이메일 폼 에러 */}
                {emailError && <div className="auth-error">{emailError}</div>}
                {activeTab === 'signup' && emailError === EMAIL_IN_USE_MESSAGE && (
                  <button type="button" onClick={switchToLoginWithEmail} className="auth-inline-action">
                    이 이메일로 로그인하기
                  </button>
                )}

                {/* 제출 버튼 */}
                <div className="auth-form__actions">
                  <button
                    type="button"
                    onClick={() => { setShowEmailForm(false); setEmailError(''); }}
                    className="auth-form__cancel"
                  >
                    취소
                  </button>
                  <button
                    type="submit"
                    disabled={emailLoading}
                    className="auth-form__submit"
                  >
                    {emailLoading ? '처리 중...' : activeTab === 'login' ? '로그인' : '가입하기'}
                  </button>
                </div>
              </form>
            </div>
          )}

          {showMethodButtons && emailFirst && (
            <>
              {methodDivider}
              {googleButton}
            </>
          )}

          {/* 이메일 인증 안내 */}
          {showEmailVerification && (
            <div className="auth-verification">
              <div className="auth-verification__icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                  <polyline points="22,6 12,13 2,6"/>
                </svg>
              </div>
              <h3 className="auth-verification__title">이메일 인증이 필요해요</h3>
              <p className="auth-verification__desc">
                <span className="auth-verification__email">{verificationEmail}</span>으로<br/>
                인증 메일을 보냈어요.<br/>
                메일의 링크를 누르면 이 화면이 자동으로 넘어가요.
              </p>
              {verificationNotice && (
                <div
                  className={verificationNotice.type === 'success' ? 'auth-success' : 'auth-error'}
                  role="status"
                >
                  {verificationNotice.text}
                </div>
              )}
              <div className="auth-verification__actions">
                <button
                  onClick={() => {
                    setShowEmailVerification(false);
                    setShowEmailForm(false);
                    setVerificationNotice(null);
                    setActiveTab('login');
                  }}
                  className="auth-verification__btn-secondary"
                >
                  취소
                </button>
                <button
                  onClick={handleResendVerification}
                  disabled={resendLoading || resendCooldown > 0}
                  className="auth-verification__btn-primary"
                >
                  {resendLoading
                    ? '발송 중...'
                    : resendCooldown > 0 ? `재발송 (${resendCooldown}초)` : '재발송'}
                </button>
              </div>
              <div className="auth-verification__tip">
                메일이 보이지 않나요? 스팸함이나 프로모션 탭을 확인해보세요.
                학교 메일(go.kr)은 늦게 오거나 막힐 수 있어서, 안 오면 Gmail·네이버 메일로 가입해보세요.
              </div>
            </div>
          )}

          {/* 비밀번호 찾기 */}
          {showPasswordReset && (
            <div className="auth-reset">
              {!resetSuccess ? (
                <form onSubmit={handlePasswordReset}>
                  <div className="auth-reset__card">
                    <div className="auth-reset__icon">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                        <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                      </svg>
                    </div>
                    <h3 className="auth-reset__title">비밀번호 찾기</h3>
                    <p className="auth-reset__desc">
                      가입하신 이메일을 입력하시면<br/>비밀번호 재설정 링크를 보내드려요.
                    </p>
                    <div className="auth-field">
                      <input
                        type="email"
                        value={resetEmail}
                        onChange={(e) => setResetEmail(e.target.value)}
                        placeholder="이메일 주소"
                        required
                        className="auth-field__input"
                      />
                    </div>
                    {emailError && <div className="auth-error">{emailError}</div>}
                    <div className="auth-form__actions">
                      <button
                        type="button"
                        onClick={() => { setShowPasswordReset(false); setResetEmail(''); setEmailError(''); }}
                        className="auth-form__cancel"
                      >
                        취소
                      </button>
                      <button
                        type="submit"
                        disabled={resetLoading}
                        className="auth-form__submit"
                      >
                        {resetLoading ? '전송 중...' : '재설정 메일 보내기'}
                      </button>
                    </div>
                  </div>
                </form>
              ) : (
                <div className="auth-reset__success-card">
                  <div className="auth-reset__success-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                      <polyline points="22 4 12 14.01 9 11.01"/>
                    </svg>
                  </div>
                  <h3 className="auth-reset__success-title">메일을 보냈어요</h3>
                  <p className="auth-reset__success-desc">
                    <strong>{resetEmail}</strong>로<br/>
                    비밀번호 재설정 링크를 보냈어요.
                  </p>
                  <button
                    onClick={() => { setShowPasswordReset(false); setResetEmail(''); setResetSuccess(false); setEmailError(''); }}
                    className="auth-reset__success-btn"
                  >
                    로그인으로 돌아가기
                  </button>
                  <div className="auth-reset__help-list">
                    <div className="auth-reset__help-card">
                      <div className="auth-reset__help-title">메일이 보이지 않나요?</div>
                      <div className="auth-reset__help-text">
                        스팸함과 프로모션함을 먼저 확인해주세요. 그래도 오지 않으면 다른 이메일로 가입했을 수 있어요.
                        Gmail, 학교 이메일, 한메일 등 자주 쓰는 계정으로도 다시 시도해보세요.
                      </div>
                    </div>
                    <div className="auth-reset__help-card auth-reset__help-card--security">
                      <div className="auth-reset__help-title">아이디 찾기는 제공하지 않아요</div>
                      <div className="auth-reset__help-text">
                        계정 보호를 위해 가입 이메일을 찾아주는 기능은 제공하지 못해요.
                        강력한 보안을 우선하는 왓퀴즈가 될게요. 양해해 주셔서 감사해요.
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* 토스 심사용 로그인 */}
          {showTossLogin && (
            <>
              <div className="auth-divider">
                <div className="auth-divider__line"></div>
                <span className="auth-divider__text">토스페이먼츠 심사용</span>
                <div className="auth-divider__line"></div>
              </div>
              <form onSubmit={handleTossLogin} className="auth-toss-form">
                <div className="auth-field">
                  <label className="auth-field__label">이메일</label>
                  <input
                    type="email"
                    value={tossEmail}
                    onChange={(e) => setTossEmail(e.target.value)}
                    placeholder="toss.review@whatquiz.co.kr"
                    required
                    className="auth-field__input"
                  />
                </div>
                <div className="auth-field">
                  <label className="auth-field__label">비밀번호</label>
                  <input
                    type="password"
                    value={tossPassword}
                    onChange={(e) => setTossPassword(e.target.value)}
                    placeholder="비밀번호"
                    required
                    className="auth-field__input"
                  />
                </div>
                <button type="submit" disabled={tossLoading} className="auth-toss-submit">
                  {tossLoading ? '로그인 중...' : '토스 심사용 로그인'}
                </button>
              </form>
              <div className="auth-toss-notice">
                토스페이먼츠 심사관님, 이 계정으로 결제 시스템을 테스트하실 수 있어요.
              </div>
            </>
          )}

          {/* 모드 전환 */}
          {showMethodButtons && (
            <div className="auth-switch">
              {activeTab === 'signup' ? (
                <>
                  이미 계정이 있나요?
                  <button onClick={() => handleTabChange('login')} className="auth-switch__link">로그인</button>
                </>
              ) : (
                <>
                  아직 계정이 없나요?
                  <button onClick={() => handleTabChange('signup')} className="auth-switch__link">회원가입</button>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* 푸터 */}
      <div className="auth-footer">
        <div className="auth-footer__inner">
          <button
            type="button"
            className="auth-footer__toggle"
            onClick={() => setShowCompanyInfo((open) => !open)}
            aria-expanded={showCompanyInfo}
          >
            {showCompanyInfo ? '회사 정보 접기' : '회사 정보 보기'}
          </button>
          <div className={`auth-footer__grid${showCompanyInfo ? ' is-open' : ''}`}>
            <div>
              <h3 className="auth-footer__section-title">플랙티컬 주식회사</h3>
              <div className="auth-footer__section-text">
                사업자등록번호: 853-81-03114<br/>
                대표자명: 강연구<br/>
                통신판매업: 2024-충남천안-0144
              </div>
            </div>
            <div>
              <h3 className="auth-footer__section-title">연락처</h3>
              <div className="auth-footer__section-text">
                대표 전화: 02-949-9158<br/>
                이메일: angel@plactical.com<br/>
                고객센터: 월~금 09:00~18:00
              </div>
            </div>
            <div>
              <h3 className="auth-footer__section-title">사업장 주소</h3>
              <div className="auth-footer__section-text">
                서울특별시 노원구 공릉로 232<br/>
                제1창업보육센터 404호
              </div>
            </div>
          </div>

          <div className="auth-footer__links">
            <a href="/company-info" className="auth-footer__link">회사소개</a>
            <a href="/privacy-policy" className="auth-footer__link">개인정보처리방침</a>
            <a href="/terms-of-service" className="auth-footer__link">이용약관</a>
            <a href="/refund-policy" className="auth-footer__link">환불정책</a>
          </div>

          <div className="auth-footer__copyright">
            © {new Date().getFullYear()} 플랙티컬 주식회사. All rights reserved.
          </div>
        </div>
      </div>

      {/* 재가입 제한 모달 */}
      {showRestrictionModal && restrictionInfo && (
        <div className="auth-modal-overlay" onClick={() => setShowRestrictionModal(false)}>
          <div className="auth-modal" onClick={(e) => e.stopPropagation()}>
            <div className="auth-modal__icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/>
                <polyline points="12 6 12 12 16 14"/>
              </svg>
            </div>
            <h3 className="auth-modal__title">재가입 대기 기간이에요</h3>
            <p className="auth-modal__desc">
              계정 보호를 위해 탈퇴 후 30일간<br/>재가입이 제한돼요.
            </p>
            <div className="auth-modal__days">
              <div className="auth-modal__days-label">재가입 가능까지</div>
              <div className="auth-modal__days-value">{restrictionInfo.daysRemaining}일</div>
            </div>
            <p className="auth-modal__notice">
              {restrictionInfo.daysRemaining}일 후에 다시 방문해주세요.<br/>
              문의: angel@plactical.com
            </p>
            <button onClick={() => setShowRestrictionModal(false)} className="auth-modal__btn">
              확인
            </button>
          </div>
        </div>
      )}

      <InAppBrowserLoginSheet
        onClose={() => setShowInAppLoginSheet(false)}
        onContinueWithEmail={() => {
          setShowEmailForm(true);
          setEmailError('');
          setShowInAppLoginSheet(false);
        }}
        open={showInAppLoginSheet}
      />
    </div>
  );
};

export default AuthPage;
